// shared-lib/shower-systems/definitions.js
//
// The shower systems a contractor can sell, one entry per maker's system —
// the data `index.js`'s rules read (claude-docs/2026-10-28-shower-systems-design.md,
// "What a system is"). Kept apart from the rules so the rules can be tested
// against a maker this list does not have (a maker known by name only).
// Pure: no I/O.

// `hex`: the solid colour a part drawn without a texture takes (a base, a
// niche) — the mean of the colour's own image. `finishes`: the wall finishes
// a colour is made in, where it is not made in every one (step 4b); its id is
// its name as a slug ("Calacatta White" → `calacatta-white`), the name its
// texture docs carry.
const slug = (name) =>
  name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
const colour = (name, hex, finishes) => ({
  id: slug(name),
  name,
  hex,
  ...(finishes ? { finishes } : {}),
});

// Sentrel's stone colours come smooth; five are printed in SimTile too
const STONE = ['smooth'];
const STONE_AND_SIMTILE = ['smooth', 'simtile-3x6', 'simtile-10x20'];

const SHOWER_SYSTEMS = Object.freeze([
  {
    // Onyx Collection (Belvue, KS): solid-colour cultured-stone bases and
    // wall panels, sold through dealers. Colours and wall finishes from
    // onyxcollection.com/color-selection and /shower-wall-panels (read
    // 2026-10-28); step 1 carries six of its 42 colours and two finishes.
    id: 'onyx-collection',
    name: 'Onyx Collection',
    maker: 'Onyx Collection',
    // its wall panels, inches: "approximately 3/8″ thick"
    // (onyxcollection.com/shower-wall-panels, read 2026-10-30). Recorded
    // only: every system's panels are drawn flat on the wall, as tile is
    // (the user, 2026-10-30).
    panelThicknessIn: 0.375,
    appliesTo: ['shower', 'tub-shower'],
    // `hex` from the centre of Onyx's own designer images
    // (import-assets/downloads/onyx-collection/designer/).
    colours: [
      colour('Adriatic', '#B3BCC2'),
      colour('Alabaster', '#DDDACD'),
      colour('Carrara', '#FAF6F6'),
      colour('Concrete', '#939393'),
      colour('Platinum', '#DFDFDB'),
      colour('Travertine', '#ABA499'),
    ],
    wallFinishes: [
      { id: 'gloss', name: 'Gloss smooth' },
      { id: 'subway', name: 'Subway tile' },
    ],
    // names this system in a contractor's price-list line — the maker's
    // whole name: onyx alone is as often a stone ("Black Onyx Quartz", an
    // onyx mosaic), and a match turns the system on at every import. Garrett's
    // list names it in full on most of its Onyx lines, and one is enough.
    linePattern: /\bonyx\s+collection\b/i,
    // …and on a line already read as a shower system's part, its bare name
    // ("Onyx Corner Seat", "Onyx Wall Panels"), which scopes the line to the
    // maker but turns nothing on (step 5)
    partPattern: /\bonyx\b/i,
    // the series its parts are named in, said or not on a contractor's line
    series: 'Liberty',
    // Its bases (onyxcollection.com/standard-*-shower-base-pan, read
    // 2026-10-29). A base is the shower's own size — standard when it is one
    // of these (`standardBaseSize`), else custom. Low profile is 1⅜–2¾" tall
    // and drawn at 2"; only mid states a width (3½"), so low and full are
    // drawn at it too.
    curbs: [
      {
        id: 'low',
        name: 'Low profile',
        heightIn: 2,
        widthIn: 3.5,
        widthAssumed: true,
      },
      { id: 'mid', name: 'Mid height', heightIn: 2.75, widthIn: 3.5 },
      {
        id: 'full',
        name: 'Full height',
        heightIn: 4,
        widthIn: 3.5,
        widthAssumed: true,
      },
    ],
    defaultCurb: 'low',
    // width × depth, inches, as the maker lists them (a size matches either
    // way round); the 60" offset-drain sizes are listed once
    baseSizes: {
      low: [
        [36, 36],
        [38, 37],
        [42, 36],
        [42, 42],
        [48, 32],
        [48, 34],
        [48, 36],
        [48, 42],
        [48, 48],
        [54, 36],
        [60, 30],
        [60, 32],
        [60, 34],
        [60, 36],
        [60, 42],
        [60, 48],
      ],
      mid: [
        [32, 32],
        [36, 32],
        [36, 34],
        [36, 36],
        [36, 42],
        [36, 48],
        [38, 37],
        [42, 32],
        [42, 34],
        [42, 36],
        [42, 42],
        [48, 30],
        [48, 32],
        [48, 34],
        [48, 36],
        [48, 42],
        [48, 48],
        [54, 32],
        [54, 36],
        [54, 42],
        [60, 30],
        [60, 32],
        [60, 34],
        [60, 36],
        [60, 42],
        [60, 48],
        [72, 36],
      ],
      full: [
        [36, 36],
        [42, 36],
        [48, 32],
        [48, 34],
        [48, 36],
        [48, 42],
        [54, 36],
        [60, 30],
        [60, 32],
        [60, 34],
        [60, 36],
      ],
    },
    // a neo-angle base, N × N along its two walls (mid curb)
    neoAngleSizes: [36, 38, 42, 48],
    // Its recessed caddies, the size on the wall with the flange; Onyx's
    // cut-out templates confirm them (each hole is this less a ⅞–1" flange,
    // import-assets/downloads/onyx-collection/templates/). They give no
    // depth: 3½", a 2×4 stud bay, until one does. `holeWidthIn` /
    // `holeHeightIn`: the template's hole, measured off the 1:1 PDF
    // (its manifest.json) — the opening the insert's flange lies around.
    niches: [
      {
        id: 'liberty-tower',
        name: 'Liberty Tower',
        // the name a contractor's list knows it by (Garrett's: "Liberty Tall
        // Niche") — the tower is the tall one
        aliases: ['Liberty Tall'],
        widthIn: 11,
        heightIn: 31.75,
        depthIn: 3.5,
        depthAssumed: true,
        holeWidthIn: 10.13,
        // its template's height spans six pages and was not assembled: the
        // flange taken as the sides' (0.87" in all)
        holeHeightIn: 30.88,
        holeHeightAssumed: true,
      },
      {
        id: 'liberty-wide',
        name: 'Liberty Wide',
        widthIn: 12.875,
        heightIn: 19.125,
        depthIn: 3.5,
        depthAssumed: true,
        holeWidthIn: 12.0,
        holeHeightIn: 18.19,
      },
      {
        id: 'liberty-ultra-wide',
        name: 'Liberty Ultra Wide',
        widthIn: 22.75,
        heightIn: 14.75,
        depthIn: 3.5,
        depthAssumed: true,
        holeWidthIn: 21.88,
        holeHeightIn: 13.75,
      },
    ],
    // Its Liberty accessories (step 3a), sizes from Onyx's install sheets
    // (CCSLV-0425 corner shelf, CSC-0425 corner caddy, CFSLV-0423 foot
    // shelf, read 2026-10-29) and, for the seats, the order form read
    // 2026-10-28 (its seat sheet is gone from the site). A corner part's
    // `sidesIn` run along the two walls and `frontIn` faces the room; the
    // extended seat is `widthIn` along the back wall by `depthIn` out from
    // it. `topIn`: its top above the shower floor. `assumed`: the fields
    // that are guesses until the maker says.
    accessories: [
      {
        id: 'liberty-corner-shelf',
        name: 'Liberty Corner Shelf',
        type: 'shower-shelf',
        shape: 'corner-shelf',
        sidesIn: 7.875,
        frontIn: 11.125,
        heightIn: 1.5,
        // Onyx: "between 42" off of the base and eye level"
        topIn: 48,
        appliesTo: ['shower', 'tub-shower'],
        assumed: ['heightIn', 'topIn'],
      },
      {
        id: 'liberty-corner-caddy',
        name: 'Liberty Corner Shower Caddy',
        type: 'corner-caddy',
        shape: 'corner-caddy',
        sidesIn: 8,
        frontIn: 11.25,
        heightIn: 20.25,
        shelves: 2,
        // Onyx: "eye level"
        topIn: 60,
        appliesTo: ['shower', 'tub-shower'],
        assumed: ['shelves', 'topIn'],
      },
      {
        id: 'liberty-tall-corner-caddy',
        name: 'Liberty Tall Corner Caddy',
        type: 'corner-caddy',
        shape: 'corner-caddy',
        sidesIn: 8,
        frontIn: 11.25,
        heightIn: 32.5,
        shelves: 3,
        topIn: 60,
        appliesTo: ['shower', 'tub-shower'],
        assumed: ['shelves', 'topIn'],
      },
      {
        id: 'liberty-corner-foot-shelf',
        name: 'Liberty Corner Foot Shelf',
        type: 'shower-shelf',
        shape: 'foot-shelf',
        sidesIn: 8,
        frontIn: 11.25,
        heightIn: 6.25,
        // Onyx: "about 9" to 12" off the shower base"
        topIn: 10.5,
        appliesTo: ['shower'],
        assumed: [],
      },
      {
        id: 'liberty-corner-seat',
        name: 'Liberty Corner Seat',
        type: 'shower-seat',
        shape: 'corner-seat',
        // the order form's 20" corner seat, read as Onyx's Regular
        sidesIn: 20,
        heightIn: 2,
        // the low end of the usual 17–19" for a shower seat
        topIn: 17,
        appliesTo: ['shower'],
        assumed: ['sidesIn', 'heightIn', 'topIn'],
      },
      {
        id: 'liberty-corner-seat-small',
        name: 'Liberty Corner Seat – Small',
        type: 'shower-seat',
        shape: 'corner-seat',
        sidesIn: 15,
        heightIn: 2,
        topIn: 17,
        appliesTo: ['shower'],
        assumed: ['sidesIn', 'heightIn', 'topIn'],
      },
      {
        id: 'liberty-extended-corner-seat',
        name: 'Liberty Extended Corner Seat',
        type: 'shower-seat',
        shape: 'extended-seat',
        widthIn: 24,
        depthIn: 13,
        heightIn: 2,
        topIn: 17,
        appliesTo: ['shower'],
        assumed: ['heightIn', 'topIn'],
      },
    ],
    // Its trim (step 3b), from the wall panel install sheet (WPI-0625) and
    // the trim sheets (INCT-0524 inside corner, LCRM-0725 crown, read
    // 2026-10-30). Panels come with finished edges and every seam takes a
    // colour-matched seam fill: the bead, drawn in each inside corner and
    // priced with the panels. The trims are optional and sold by the foot.
    // `runs`: 'corners' up each inside corner to the panels' top, 'top'
    // along every wall at it, 'band' along every wall at `centreIn` up.
    // `piece`: which piece of the standard's solid-surface trim it is
    // (`install-solid-surface-trim`, labor/axes.js `piece`). Crown is
    // Liberty 3", as the niches and accessories are Liberty.
    bead: { radiusIn: 0.25 },
    trims: [
      {
        id: 'inside-corner',
        name: 'Inside Corner Trim',
        profileIn: { height: 1, out: 1 },
        runs: 'corners',
        piece: 'corner',
        assumed: [],
      },
      {
        id: 'crown',
        name: 'Crown Molding',
        profileIn: { height: 3, out: 1.875 },
        runs: 'top',
        piece: 'crown',
        assumed: [],
      },
      {
        id: 'chair-rail',
        name: 'Chair Rail',
        profileIn: { height: 2.5, out: 0.75 },
        runs: 'band',
        piece: 'band',
        centreIn: 42,
        assumed: ['profileIn.out', 'centreIn'],
      },
    ],
    // A window inside its panels (`WINDOW_TRIM`, 2026-10-31): a cap piece
    // on the sill and trim on the other three sides — Garrett's "Pony Wall
    // Cap" is "a cap piece of Onyx Collection on the windowsill and pony
    // wall".
    windowTrim: { sill: 'cap' },
  },
  // Step 4a (2026-10-30) named these two makers — a contractor's line
  // naming one narrowed to it on import (`price-import/showerSystemLines.js`)
  // — and step 4b gave them their colours, so they are ready (`isSystemReady`):
  // offered, chosen and switched on by an import.
  {
    // Sentrel (sentrelproducts.com): printed PVC wall panels in 20+ colours
    // and SimTile patterns, shower bases (standard sizes; custom to 60 × 96),
    // shelves, seats, a bench, recessed caddies and trims
    id: 'sentrel',
    name: 'Sentrel',
    maker: 'Sentrel',
    // its stone panels, SimTile printed on them: "¼″ solid composite"
    // (sentrelproducts.com/composite-vs-acrylic-shower-wall-panels, read
    // 2026-10-30) — Dimensions SimTile, acrylic, says its own below
    panelThicknessIn: 0.25,
    appliesTo: ['shower', 'tub-shower'],
    // Step 4b (2026-10-30): its colours from sentrelproducts.com/products/
    // colors/ (stone, SimTile, Dimensions SimTile), `hex` the mean of each
    // colour's own texture. SimTile is printed in five of the stones; the
    // Dimensions SimTile is a white panel routed with grooves, its grout
    // gray or black.
    colours: [
      colour('Alaskan Ivory', '#C7B49E', STONE),
      colour('Azzurra Bay', '#F0F2F3', STONE),
      colour('Bardiglio', '#808594', STONE),
      colour('Black Mist', '#181819', STONE),
      colour('Botticino Cream', '#F9F4EB', STONE),
      colour('Calabria', '#E8E2D6', STONE),
      colour('Calacatta White', '#E1E1E1', STONE_AND_SIMTILE),
      colour('Creme Travertine', '#DACBB5', STONE),
      colour('Everest', '#C4C6CB', STONE),
      colour('Frost', '#E8E8E8', STONE_AND_SIMTILE),
      colour('Golden Beaches', '#C0A98F', STONE),
      colour('Gray Quartz', '#DADADC', STONE),
      colour('Lincoln Gold', '#EAF1F3', STONE),
      colour('Mocha Travertine', '#988771', STONE_AND_SIMTILE),
      colour('Taj Mahal', '#E9EBE6', STONE),
      colour('Triton', '#9DACAF', STONE),
      colour('Veincut Grey', '#E1E1E1', STONE_AND_SIMTILE),
      colour('White Pearl', '#F0F0F1', STONE_AND_SIMTILE),
      {
        ...colour('Dimensions SimTile', '#FBFBFB', [
          '3x8-gray',
          '3x8-black',
          '10x20-gray',
          '10x20-black',
        ]),
        // "a two-layered acrylic shower wall", not the stone composite;
        // Sentrel publishes no thickness for it
        panelThicknessIn: null,
      },
    ],
    wallFinishes: [
      { id: 'smooth', name: 'Stone (smooth)' },
      { id: 'simtile-3x6', name: 'SimTile 3" x 6" subway' },
      { id: 'simtile-10x20', name: 'SimTile 10" x 20" block' },
      { id: '3x8-gray', name: '3" x 8", gray grout' },
      { id: '3x8-black', name: '3" x 8", black grout' },
      { id: '10x20-gray', name: '10" x 20", gray grout' },
      { id: '10x20-black', name: '10" x 20", black grout' },
    ],
    linePattern: /\bsentrel\b/i,
    // Its bases: "all standard shower base sizes and custom shower pans made
    // to fit any size up to 60" x 96"" (sentrelproducts.com), "Available in
    // all Sentrel colors". The standard sizes are the ones its own pages
    // name; it publishes no threshold height or width — a low-profile curb
    // drawn as Onyx's low one.
    curbs: [
      {
        id: 'low',
        name: 'Low profile',
        heightIn: 2,
        widthIn: 3.5,
        heightAssumed: true,
        widthAssumed: true,
      },
    ],
    defaultCurb: 'low',
    baseSizes: {
      low: [
        [30, 60],
        [32, 60],
        [36, 36],
        [36, 48],
        [36, 60],
      ],
    },
    // Its recessed caddies, read as width x height from the site's names
    // ("Recessed Caddy 17x17", "17x26"; the brochure's "Recessed Caddy" and
    // "Large Recessed Caddy"); no depth published — a 2x4 stud bay's.
    niches: [
      {
        id: 'recessed-caddy',
        name: 'Recessed Caddy',
        widthIn: 17,
        heightIn: 17,
        depthIn: 3.5,
        depthAssumed: true,
      },
      {
        id: 'large-recessed-caddy',
        name: 'Large Recessed Caddy',
        widthIn: 17,
        heightIn: 26,
        depthIn: 3.5,
        depthAssumed: true,
      },
    ],
    // Its accessories by name (sentrelproducts.com/products/accessories/,
    // the brochure pp. 14-15): no size is published for any, so every size
    // is a guess, drawn as Onyx's like parts.
    accessories: [
      {
        id: 'corner-shelf',
        name: 'Corner Shelf',
        type: 'shower-shelf',
        shape: 'corner-shelf',
        sidesIn: 8,
        frontIn: 11.25,
        heightIn: 1.5,
        topIn: 48,
        appliesTo: ['shower', 'tub-shower'],
        assumed: ['sidesIn', 'frontIn', 'heightIn', 'topIn'],
      },
      {
        id: 'shave-shelf',
        name: 'Shave Shelf',
        type: 'shower-shelf',
        shape: 'foot-shelf',
        sidesIn: 8,
        frontIn: 11.25,
        heightIn: 1.5,
        topIn: 12,
        appliesTo: ['shower'],
        assumed: ['sidesIn', 'frontIn', 'heightIn', 'topIn'],
      },
      {
        id: 'corner-seat',
        name: 'Corner Seat',
        type: 'shower-seat',
        shape: 'corner-seat',
        sidesIn: 15,
        heightIn: 2,
        topIn: 17,
        appliesTo: ['shower'],
        assumed: ['sidesIn', 'heightIn', 'topIn'],
      },
    ],
    // its "Shower Bench" (named only; Garrett prices "Sentrel Bench -
    // Built-In"): a bench in a Sentrel shower's group is Sentrel's, priced
    // under it. Drawn as the shower's own bench is, for now.
    bench: { id: 'shower-bench', name: 'Shower Bench', assumed: [] },
    // a window inside its panels is trimmed all round ("Sentrel Window
    // Trim", `WINDOW_TRIM`)
    windowTrim: { sill: 'trim' },
  },
  {
    // Wilsonart Wetwall (wilsonart.com/wetwall): panels 8–60" wide × 72 or
    // 96" tall, edges that need no trim, colour-matched sealant, and solid
    // surface niches and a corner shelf; no base. Wilsonart alone is as
    // often a counter's laminate, so its full name is "Wilsonart" with "wet
    // wall", or "Wetwall"; on a line read as a shower part, "Wilsonart"
    id: 'wilsonart-wetwall',
    name: 'Wilsonart Wetwall',
    maker: 'Wilsonart',
    // "Panel Thickness: 0.41-inch nominal" (Wilsonart's specification
    // guide 10 21 26, revised 2026-04-09)
    panelThicknessIn: 0.41,
    appliesTo: ['shower', 'tub-shower'],
    // Step 4b (2026-10-30): its 19 current designs from the design library
    // (wilsonart.com/wetwall/design-library), in its groups' order — solids,
    // stones, patterns and textiles, woodgrains. Each design comes in ONE
    // finish, fixed by the maker; `hex` the mean of its own texture.
    colours: [
      colour('Aria White', '#FAFAF9', ['linearity']),
      colour('Tranquil Lines', '#F8F8F8', ['linearity']),
      colour('Graceful Lines', '#E6E5E0', ['linearity']),
      colour('Tuscany Marble', '#EFEDEB', ['antique']),
      colour('Harmony Mist', '#D9D4CC', ['antique']),
      colour('Tahiti Sands', '#D9D9D9', ['antique']),
      colour('Torrone Marble', '#E7E7DF', ['textured-gloss']),
      colour('Augusta Calacatta', '#ECECE4', ['textured-gloss']),
      colour('Calacatta Statuario', '#DFE0E2', ['glaze']),
      colour('Urban Soapstone', '#99968F', ['antique']),
      colour('Midnight Elegance', '#212024', ['antique']),
      colour('Staccato', '#E5E3DC', ['linearity']),
      colour('Silken Weave', '#F1F2ED', ['leno-weave']),
      colour('Serenity Weave', '#E0DCD0', ['leno-weave']),
      colour('Classic Weave', '#D4D4CB', ['leno-weave']),
      colour('Ashen Walnut', '#E9E6DA', ['timbergrain']),
      colour('Darlington Cherry', '#E3D8C9', ['silken-wood']),
      colour('Champagne Whisper', '#D4CAB6', ['gloss-line']),
      colour('Legacy Ash', '#C0BCB0', ['softgrain']),
    ],
    wallFinishes: [
      { id: 'linearity', name: '#18 Linearity' },
      { id: 'antique', name: '#22 Antique' },
      { id: 'textured-gloss', name: '#07 Textured Gloss' },
      { id: 'glaze', name: '#55 Glaze' },
      { id: 'leno-weave', name: '#19 Leno Weave' },
      { id: 'timbergrain', name: '#05 Timbergrain' },
      { id: 'silken-wood', name: '#17 Silken Wood' },
      { id: 'gloss-line', name: '#28 Gloss Line' },
      { id: 'softgrain', name: '#12 SoftGrain' },
    ],
    // "Wilsonart" with its wet walls or shower walls, either order; or the
    // one-word brand "Wetwall" beside panels or a shower — never "wet wall"
    // alone, which is any shower's (a framer's, a demolition's, another
    // maker's wet wall panels) (step 4b, final review)
    linePattern:
      /\bwilsonart\b.*\b(?:wet[\s-]*walls?|shower\s+walls?)\b|\b(?:wet[\s-]*walls?|shower\s+walls?)\b.*\bwilsonart\b|\bwetwalls?\b.*\b(?:panels?|shower)\b|\b(?:panels?|shower)\b.*\bwetwalls?\b/i,
    partPattern: /\bwilsonart\b|\bwetwalls?\b/i,
    // no base of its own: a Wetwall shower stands on the plain prefab pan
    // (the user, 2026-10-30, ruling A). Its niches and corner shelf are
    // Wilsonart Solid Surface in Designer White or Antique White only, never
    // the panel's colour — drawn in Designer White, its hex the plain pan's
    // (Wilsonart states none)
    partColour: { name: 'Designer White', hex: '#F4F3EF', assumed: ['hex'] },
    // Its Wilsonart Solid Surface niches and corner shelf (the Wetwall sales
    // flyer); no niche depth or shelf thickness published. "Recommend the
    // Corner Shelf would be installed 43" from the shower floor" (its install
    // guide). A contractor's list calls a niche by its tiers.
    niches: [
      {
        id: 'double-shelf',
        name: 'Double Shelf Recessed',
        aliases: ['Double Tier'],
        widthIn: 10.21875,
        heightIn: 15.75,
        depthIn: 3.5,
        depthAssumed: true,
      },
      {
        id: 'single-shelf',
        name: 'Single Shelf Recessed',
        aliases: ['Single Tier'],
        widthIn: 8.1667,
        heightIn: 12.125,
        depthIn: 3.5,
        depthAssumed: true,
      },
    ],
    accessories: [
      {
        id: 'corner-shelf',
        name: 'Corner Shelf',
        type: 'shower-shelf',
        shape: 'corner-shelf',
        // a 7 7/8" right triangle; its front the hypotenuse
        sidesIn: 7.875,
        frontIn: 11.125,
        heightIn: 1,
        topIn: 43,
        appliesTo: ['shower', 'tub-shower'],
        assumed: ['frontIn', 'heightIn'],
      },
    ],
    // no trim: its panels' edges meet in the corners on a colour-matched
    // sealant, drawn as a bead — and a window inside them takes none either
    // (no `windowTrim`)
    bead: { radiusIn: 0.125, assumed: true },
  },
  {
    // An acrylic shower: generic at first (the user, 2026-10-30, "a basic
    // white acrylic"; claude-docs/2026-10-30-shower-systems-acrylic-plan.md),
    // BCI Acrylic's since 2026-11-01 (the user: "import all the colors then,
    // used exclusively for the bci products") — Bath Concepts, Libertyville
    // IL, one of the larger independent makers of acrylic walls for
    // remodelers, sold through dealers. Its parts' rows and panels carry the
    // maker as their brand, as Onyx's and Sentrel's do. Last in this list: a
    // line naming another maker as well is that maker's.
    id: 'acrylic',
    name: 'Acrylic',
    maker: 'BCI Acrylic',
    // "acrylic" is an ordinary word — in a demolition, a tub, a bare
    // "walls" — so only a line naming one of its parts names it
    // (`systemsNamedByLines`, final review 2026-10-30)
    namedOnlyByParts: true,
    // BCI publishes none
    panelThicknessIn: null,
    appliesTo: ['shower', 'tub-shower'],
    // BCI's colours (bathconcepts.com/products/colors-patterns, read
    // 2026-10-31), smooth; `hex` the mean of each built tile, or the chip's
    // own colour for a plain one. White stays the plain prefab pan's white,
    // so walls and pan match (BCI's "White (Matte or Glossy)"); London Fog
    // is left out — its page shows the 3x6 Subway pattern's image, so there
    // is no picture of it. BCI's patterns (subway, tile, beadboard) are not
    // carried yet.
    colours: [
      colour('White', '#F4F3EF'),
      colour('Brecchia/Sierra Sand', '#D1BBA4'),
      colour('Madeira/Santa Cruz Granite', '#AF967D'),
      colour('River Rock/Canyon Rock', '#BF9B83'),
      colour('Travertine', '#DECDBB'),
      colour('Lightning', '#D5D5D0'),
      colour('Quartz', '#BBB8B3'),
      colour('Silver White', '#F1EADB'),
      colour('Gray', '#D7D7D7'),
      colour('Almond', '#F9EED8'),
      colour('Biscuit', '#F8F0D4'),
      colour('Sandbar', '#CBBBA4'),
      colour('Arctic Ice', '#D1C9C1'),
      colour('BiancoTravertine', '#CABFAD'),
      colour('Calcutta Marble', '#F5F6F1'),
      colour('Carbon Ash Classic Concrete', '#CAC7C2'),
      colour('Evo', '#D5D2CB'),
      colour('Galena', '#F2F2F0'),
      colour('Glacier Ice', '#ECE9E6'),
      colour('Horizon Beige', '#C2BEB2'),
      colour('Metapeake', '#D8D5D0'),
      colour('Napoli Marble/Pompeii Marble', '#D2CCBF'),
      colour('San Michele', '#D6D1C4'),
      colour('Sandalwood', '#BEA791'),
      colour('Santorini White Marble', '#F6F7F4'),
      colour('Tuscany', '#F5F0EB'),
      colour('Versailles', '#D5D2CA'),
      // white subway with a coloured grout line, which BCI lists as colours
      colour('Grayline', '#EFEFEE'),
      colour('Othello', '#D1D1D1'),
    ],
    wallFinishes: [{ id: 'smooth', name: 'Smooth' }],
    // "acrylic" before a shower part's word: an acrylic tub, a demolition
    // or a window's trim alone never names it (nor switches it on)
    linePattern:
      /\bacrylic\b.*\b(?:showers?|walls?|pans?|niches?|shel(?:f|ves)|caddy|caddies|bench(?:es)?|seats?|grab\s*bars?)\b/i,
    partPattern: /\bacrylic\b/i,
    // no base of its own: it stands on the plain prefab pan, as Wetwall
    // does (ruling A), priced on the general prefab-pan lines
    //
    // Its parts, named for a contractor's lines (Garrett's Acrylic lines);
    // no maker states a size, so every one is a guess. The niche fits one
    // stud bay (12" of a 14½" bay); its width and height are assumed too.
    niches: [
      {
        id: 'recessed',
        name: 'Recessed',
        aliases: ['Niche'],
        widthIn: 12,
        heightIn: 24,
        depthIn: 3.5,
        depthAssumed: true,
      },
    ],
    // drawn by the shapes the other systems' parts use, plus a tower shelf
    // (a caddy's boards, its bottom board the footrest) and a corner shelf
    // with a grab bar along its front
    accessories: [
      {
        id: 'corner-shelf',
        name: 'Corner Shelf',
        type: 'shower-shelf',
        shape: 'corner-shelf',
        sidesIn: 8,
        frontIn: 11.25,
        heightIn: 1.5,
        topIn: 48,
        appliesTo: ['shower', 'tub-shower'],
        assumed: ['sidesIn', 'frontIn', 'heightIn', 'topIn'],
      },
      {
        id: 'tower-shelf',
        name: 'Tower Shelf with Footrest',
        type: 'shower-shelf',
        shape: 'tower-shelf',
        sidesIn: 8,
        frontIn: 11.25,
        // 12" to 60" up: its bottom board, 12" up, is the footrest
        heightIn: 48,
        shelves: 2,
        topIn: 60,
        appliesTo: ['shower'],
        assumed: ['sidesIn', 'frontIn', 'heightIn', 'shelves', 'topIn'],
      },
      {
        id: 'corner-caddy',
        name: '4-Shelf Corner Caddy',
        type: 'corner-caddy',
        shape: 'corner-caddy',
        sidesIn: 8,
        frontIn: 11.25,
        heightIn: 32.5,
        // its bottom, two between and its top: four boards
        shelves: 2,
        topIn: 60,
        appliesTo: ['shower', 'tub-shower'],
        assumed: ['sidesIn', 'frontIn', 'heightIn', 'topIn'],
      },
      {
        id: 'full-floating-bench',
        name: 'Full Floating Bench',
        type: 'shower-seat',
        shape: 'extended-seat',
        widthIn: 36,
        depthIn: 15,
        heightIn: 2,
        topIn: 17,
        appliesTo: ['shower'],
        assumed: ['widthIn', 'depthIn', 'heightIn', 'topIn'],
      },
      {
        // drawn as the triangle corner seat
        id: 'hexagon-bench',
        name: 'Hexagon Bench',
        type: 'shower-seat',
        shape: 'corner-seat',
        sidesIn: 18,
        heightIn: 2,
        topIn: 17,
        appliesTo: ['shower'],
        assumed: ['sidesIn', 'heightIn', 'topIn'],
      },
      {
        // a grab bar part: a contractor's line reads as a grab bar
        id: 'corner-shelf-grab-bar',
        name: 'Corner Shelf Grab Bar',
        type: 'grab-bar',
        shape: 'shelf-grab-bar',
        sidesIn: 12,
        frontIn: 17,
        heightIn: 1.5,
        // at a grab bar's height
        topIn: 36,
        appliesTo: ['shower', 'tub-shower'],
        assumed: ['sidesIn', 'frontIn', 'heightIn', 'topIn'],
      },
    ],
    // its panels meet in the corners on a sealant bead
    bead: { radiusIn: 0.125, assumed: true },
    // a window inside its panels is trimmed all round ("Acrylic Window
    // Trim", `WINDOW_TRIM`)
    windowTrim: { sill: 'trim' },
  },
]);

module.exports = SHOWER_SYSTEMS;
